import { useState, useCallback, useMemo, useEffect } from 'react'
import { ScheduleData, SchedType } from '../api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import {
  Clock, Timer, CalendarClock, Repeat, Play, Calendar,
  ChevronDown, Loader2, Save, BookOpen,
} from 'lucide-react'

interface Props {
  open: boolean
  onClose: () => void
  initial: ScheduleData
  onSave: (data: ScheduleData) => Promise<void>
  // Bug #7 follow-up: scheduler also gates on the parent toggle
  // (`Автозапуск по расписанию`) in ProjectModal. If the toggle is OFF,
  // SCHED_ENABLED will land as "Нет" and no autostart fires regardless
  // of perfect work_hours/start_time setup. We surface this from the
  // dialog so the user doesn't get silent no-op.
  parentEnabled?: boolean
}

const SCHED_TYPES: { value: SchedType; label: string; desc: string; icon: typeof Clock }[] = [
  { value: 'time', label: 'Запуск по времени', desc: 'Запускать каждый день в указанное время', icon: Clock },
  { value: 'interval', label: 'С интервалом', desc: 'Работа → пауза → повтор циклически', icon: Timer },
  { value: 'hourly', label: 'Каждый час', desc: 'Запускать в начале каждого часа', icon: Repeat },
  { value: 'daily', label: 'Ежедневно', desc: 'Один запуск в день в заданное время', icon: CalendarClock },
  { value: 'continuous', label: 'Непрерывно', desc: 'Работает без остановки, авто-рестарт при сбое', icon: Play },
]

const DAY_OPTIONS = [
  { value: 'Каждый день', label: 'Каждый день' },
  { value: 'Пн-Пт', label: 'Будни (Пн–Пт)' },
  { value: 'Сб-Вс', label: 'Выходные (Сб–Вс)' },
  { value: 'Выбранные дни', label: 'Выбранные дни' },
]

const WEEKDAYS = [
  { key: 'пн', label: 'Пн' },
  { key: 'вт', label: 'Вт' },
  { key: 'ср', label: 'Ср' },
  { key: 'чт', label: 'Чт' },
  { key: 'пт', label: 'Пт' },
  { key: 'сб', label: 'Сб' },
  { key: 'вс', label: 'Вс' },
]


// ── In-dialog manual ────────────────────────────────────────────────
//
// Mirrors the look-and-feel of `ApiDocumentation` from
// DynamicTabBooster.tsx — same collapsible card pattern, same colour
// palette. We reuse it so users get a familiar "click to expand"
// affordance the moment they open the scheduler.
function SchedulerManual() {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <BookOpen className="w-4 h-4 shrink-0 text-primary" />
          <span className="text-sm font-semibold text-foreground truncate">Как работает планировщик · краткий гайд</span>
        </div>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform text-muted-foreground ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 text-sm leading-relaxed border-t border-border pt-3">

          <div>
            <h4 className="font-semibold text-foreground mb-1.5">Зачем нужно</h4>
            <p className="text-muted-foreground">
              Чтобы проект сам стартовал и сам останавливался по нужному вам графику —
              в фиксированное время, циклически, по расписанию рабочих часов или
              без перерывов вообще. Не нужно держать вкладку открытой и нажимать
              «Запуск» вручную.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Какой тип выбрать</h4>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li><b className="text-foreground">Запуск по времени</b> — раз в день, в указанное время. Подойдёт, если нужно стартовать строго в условные 09:00.</li>
              <li><b className="text-foreground">С интервалом</b> — поработал N часов / N минут → пауза → снова поработал. Циклически. Хорош для дозированной нагрузки.</li>
              <li><b className="text-foreground">Каждый час</b> — старт в указанную минуту каждого часа (например в :15). Полезно для коротких регулярных запусков.</li>
              <li><b className="text-foreground">Ежедневно</b> — то же что «Запуск по времени», но строго один раз в сутки.</li>
              <li><b className="text-foreground">Непрерывно</b> — крутится без остановок, при сбое автоматически рестартует через указанную задержку.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Время запуска <span className="text-muted-foreground font-normal">vs</span> Рабочие часы</h4>
            <p className="text-muted-foreground text-xs mb-1.5">
              Это <b>два разных</b> ограничения, которые работают одновременно:
            </p>
            <ul className="space-y-1 text-xs text-muted-foreground list-disc list-inside ml-1">
              <li><b className="text-foreground">Время запуска</b> — конкретный момент, когда стартует кампания (для «по времени», «ежедневно»).</li>
              <li><b className="text-foreground">Рабочие часы</b> — окно «когда вообще можно работать». Вне этого окна проект автоматически останавливается, даже если запущен.</li>
            </ul>
            <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <b>Важно:</b> если «Время запуска» (например 08:00) попадает <i>вне</i> «Рабочих часов»
              (например 09:00–18:00) — автостарт никогда не сработает. Поставьте время
              запуска внутри окна или расширьте окно. Диалог покажет жёлтое
              предупреждение, если такое расхождение есть.
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Дни работы</h4>
            <p className="text-muted-foreground text-xs">
              «Каждый день», «Будни», «Выходные» или произвольный набор. В выходные/рабочие
              дни кампания запускается только если день попадает в выбранный фильтр.
              По умолчанию — каждый день.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Лимиты длительности и количества</h4>
            <ul className="space-y-1 text-xs text-muted-foreground list-disc list-inside ml-1">
              <li><b className="text-foreground">Макс. время работы за запуск</b> — после этого времени проект сам остановится. <code className="bg-muted px-1 rounded">0 ч 0 мин</code> = без ограничения.</li>
              <li><b className="text-foreground">Макс. количество запусков</b> — для интервала/непрерывно: после N перезапусков планировщик прекращает их. <code className="bg-muted px-1 rounded">0</code> = без ограничения.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Что делает «Стоп» вручную</h4>
            <p className="text-muted-foreground text-xs">
              Если вы остановили проект вручную, планировщик <b>не</b> поднимет его обратно
              в этом же рабочем окне — даже в «непрерывном» режиме. Проект снова
              включится автоматически:
            </p>
            <ul className="space-y-0.5 text-xs text-muted-foreground list-disc list-inside ml-1 mt-1">
              <li>когда наступит следующий рабочий день (если задан фильтр дней),</li>
              <li>когда откроется следующее «Рабочее окно» (если задан интервал часов),</li>
              <li>когда вы измените настройки расписания и сохраните,</li>
              <li>либо когда вы сами нажмёте «Запуск».</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-1.5">Главный переключатель</h4>
            <p className="text-muted-foreground text-xs">
              В настройках проекта есть тумблер <b className="text-foreground">«Автозапуск
              по расписанию»</b>. Без него настройки этого диалога не вступят в силу.
              Если он выключен — внутри будет жёлтое предупреждение.
            </p>
          </div>

        </div>
      )}
    </div>
  )
}

export default function SchedulerDialog({ open, onClose, initial, onSave, parentEnabled = true }: Props) {
  const [saving, setSaving] = useState(false)
  // Bug #75: dialog now has its own enabled toggle. Default = the
  // value passed in (so loaded settings keep their state). Operator
  // can flip it OFF here without going back to ProjectModal.
  const [enabled, setEnabled] = useState(initial.enabled !== false)
  const [type, setType] = useState<SchedType>(initial.type || 'time')

  // Bug #77: re-sync local state when `initial` changes OR dialog re-opens.
  // Without this, opening the dialog second time after saving "С интервалом"
  // showed stale "Запуск по времени" because useState above only fires once.
  useEffect(() => {
    if (open) {
      setEnabled(initial.enabled !== false)
      setType((initial.type as SchedType) || 'time')
      setStartTime(initial.start_time || '09:00')
      setStartRandom(initial.start_random || false)
      setStartTimeTo(initial.start_time_to || '')
      setDays(initial.days || 'Каждый день')
      setDaysCustom(initial.days_custom || '')
      setWorkFrom(initial.work_from || '00:00')
      setWorkTo(initial.work_to || '23:59')
      setRunHours(initial.run_hours ?? 0)
      setRunMinutes(initial.run_minutes ?? 0)
      setIntervalMinutes(initial.interval_minutes ?? 10)
      setMaxRuns(initial.max_runs ?? 0)
      setRestartDelay(initial.restart_delay ?? 5)
      setHourlyMinute(initial.hourly_minute ?? 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial.type, initial.enabled, initial.start_time, initial.interval_minutes,
      initial.run_hours, initial.run_minutes, initial.work_from, initial.work_to,
      initial.days, initial.days_custom, initial.max_runs, initial.restart_delay,
      initial.hourly_minute,
      // Bug #193: без этих двух deps тоггл «Случайное время старта» не
      // пере-синкался при поздней/изменённой загрузке конфига → при повторном
      // открытии показывался выключенным, хотя SCHED_START_RANDOM='Да' сохранён.
      initial.start_random, initial.start_time_to])

  const [startTime, setStartTime] = useState(initial.start_time || '09:00')
  const [startRandom, setStartRandom] = useState(initial.start_random || false)
  const [startTimeTo, setStartTimeTo] = useState(initial.start_time_to || '')
  const [days, setDays] = useState(initial.days || 'Каждый день')
  const [daysCustom, setDaysCustom] = useState(initial.days_custom || '')
  const [workFrom, setWorkFrom] = useState(initial.work_from || '00:00')
  const [workTo, setWorkTo] = useState(initial.work_to || '23:59')
  const [runHours, setRunHours] = useState(initial.run_hours ?? 0)
  const [runMinutes, setRunMinutes] = useState(initial.run_minutes ?? 0)
  const [intervalMinutes, setIntervalMinutes] = useState(initial.interval_minutes ?? 10)
  const [maxRuns, setMaxRuns] = useState(initial.max_runs ?? 0)
  const [restartDelay, setRestartDelay] = useState(initial.restart_delay ?? 5)
  const [hourlyMinute, setHourlyMinute] = useState(initial.hourly_minute ?? 0)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const customDaysSet = useMemo(() => new Set(daysCustom.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)), [daysCustom])

  const toggleCustomDay = useCallback((day: string) => {
    const s = new Set(customDaysSet)
    if (s.has(day)) s.delete(day); else s.add(day)
    setDaysCustom(Array.from(s).join(','))
  }, [customDaysSet])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await onSave({
        enabled,
        type,
        start_time: startTime,
        start_random: startRandom,
        start_time_to: startTimeTo,
        days,
        days_custom: daysCustom,
        work_from: workFrom,
        work_to: workTo,
        run_hours: runHours,
        run_minutes: runMinutes,
        interval_minutes: intervalMinutes,
        max_runs: maxRuns,
        restart: type === 'interval' || type === 'continuous',
        restart_delay: type === 'interval' ? intervalMinutes : restartDelay,
        restart_max: maxRuns,
        hourly_minute: hourlyMinute,
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }, [enabled, type, startTime, startRandom, startTimeTo, days, daysCustom, workFrom, workTo, runHours, runMinutes, intervalMinutes, maxRuns, restartDelay, hourlyMinute, onSave, onClose])

  const summary = useMemo(() => {
    const t = SCHED_TYPES.find(s => s.value === type)
    const parts: string[] = []
    if (t) parts.push(t.label)
    if (type === 'time' || type === 'daily') parts.push(startRandom ? `в ${startTime}–${startTimeTo || startTime} (случайно)` : `в ${startTime}`)
    if (type === 'hourly') parts.push(`в :${String(hourlyMinute).padStart(2, '0')} каждого часа`)
    if (type === 'interval') {
      if (runHours || runMinutes) parts.push(`работа ${runHours}ч ${runMinutes}м`)
      parts.push(`пауза ${intervalMinutes} мин`)
    }
    if (days !== 'Каждый день') parts.push(days === 'Выбранные дни' ? daysCustom || 'дни не выбраны' : days)
    if (workFrom !== '00:00' || workTo !== '23:59') parts.push(`часы ${workFrom}–${workTo}`)
    if (maxRuns > 0) parts.push(`макс. ${maxRuns} запусков`)
    return parts.join(' · ')
  }, [type, startTime, startRandom, startTimeTo, hourlyMinute, runHours, runMinutes, intervalMinutes, days, daysCustom, workFrom, workTo, maxRuns])

  return (
    <AlertDialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <AlertDialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Настройка расписания
          </AlertDialogTitle>
          <AlertDialogDescription>
            Выберите тип расписания и настройте параметры запуска проекта
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-5 py-2">
          {/* Inline manual: open by default-collapsed, opens with one click.
              Stays at the top so first-time users see it immediately. */}
          <SchedulerManual />

          {/* Bug #75: master enable/disable toggle right inside the dialog
              so the operator doesn't need to dig through ProjectModal. */}
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card/30 px-4 py-3">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-foreground">Автозапуск по расписанию</span>
              <span className="text-xs text-muted-foreground">
                {enabled
                  ? 'Включён — проект будет запускаться/останавливаться автоматически'
                  : 'Выключен — расписание сохранится, но не сработает'}
              </span>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {!parentEnabled && enabled && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
              ⚠ В настройках проекта родительский переключатель «Автозапуск по расписанию» <b>выключен</b>.
              Сохранение здесь обновит и его — расписание сразу заработает.
            </div>
          )}

          {/* Schedule type selection */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Тип расписания</Label>
            <div className="space-y-1.5">
              {SCHED_TYPES.map(st => {
                const Icon = st.icon
                const active = type === st.value
                return (
                  <button
                    key={st.value}
                    onClick={() => setType(st.value)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer ${
                      active
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border hover:border-primary/30 hover:bg-muted/30'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                      active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${active ? 'text-foreground' : 'text-foreground/80'}`}>{st.label}</span>
                        {active && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                      </div>
                      <span className="text-xs text-muted-foreground">{st.desc}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          <Separator />

          {/* Type-specific settings */}
          <div className="space-y-4">
            {(type === 'time' || type === 'daily') && (
              <div className="space-y-2">
                <Label>Время запуска</Label>
                <div className="flex items-center gap-2">
                  <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="w-32 tabular-nums" />
                  {startRandom && (
                    <>
                      <span className="text-xs text-muted-foreground">до</span>
                      <Input type="time" value={startTimeTo || startTime} onChange={e => setStartTimeTo(e.target.value)} className="w-32 tabular-nums" />
                    </>
                  )}
                </div>
                {/* Idea #65: random start within a window — each day the
                    campaign launches at a random moment between the two times. */}
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={startRandom} onChange={e => setStartRandom(e.target.checked)} className="accent-primary" />
                  <span className="text-xs text-foreground">Случайное время старта (в интервале)</span>
                </label>
                {startRandom && (
                  <p className="text-xs text-muted-foreground">
                    Каждый день кампания будет стартовать в случайный момент между {startTime} и {startTimeTo || startTime} — больше рандома.
                  </p>
                )}
                {/* Bug #7: scheduler also gates on "Рабочие часы" below.
                    If start time is outside that window, autostart never
                    fires. Warn the user explicitly. */}
                {(() => {
                  const inRange = (t: string, from: string, to: string) => {
                    const m = (s: string) => {
                      const [h, mi] = s.split(':').map(Number)
                      return (h || 0) * 60 + (mi || 0)
                    }
                    return m(t) >= m(from) && m(t) <= m(to)
                  }
                  const ok = inRange(startTime, workFrom, workTo)
                  if (ok) return null
                  return (
                    <p className="text-xs text-amber-500">
                      ⚠ Время запуска ({startTime}) вне рабочих часов ({workFrom}–{workTo}).
                      Автостарт не сработает — поправь рабочие часы ниже или измените время запуска.
                    </p>
                  )
                })()}
              </div>
            )}

            {type === 'interval' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Время работы (часы)</Label>
                    <Input type="number" min={0} max={24} value={runHours} onChange={e => { const v = parseInt(e.target.value, 10); const c = isNaN(v) ? 0 : Math.min(Math.max(v, 0), 24); setRunHours(c); e.target.value = String(c) }} />
                  </div>
                  <div className="space-y-2">
                    <Label>Время работы (мин)</Label>
                    <Input type="number" min={0} max={59} value={runMinutes} onChange={e => { const v = parseInt(e.target.value, 10); const c = isNaN(v) ? 0 : Math.min(Math.max(v, 0), 59); setRunMinutes(c); e.target.value = String(c) }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Пауза между запусками (мин)</Label>
                  <Input type="number" min={1} max={1440} value={intervalMinutes} onChange={e => setIntervalMinutes(+e.target.value)} className="w-32" />
                  <p className="text-xs text-muted-foreground">После завершения работы проект будет перезапущен через указанное время</p>
                </div>
              </div>
            )}

            {type === 'hourly' && (
              <div className="space-y-2">
                <Label>Минута запуска (0–59)</Label>
                <Input type="number" min={0} max={59} value={hourlyMinute} onChange={e => setHourlyMinute(+e.target.value)} className="w-32" />
                <p className="text-xs text-muted-foreground">Проект будет запускаться в :{String(hourlyMinute).padStart(2, '0')} каждого часа</p>
              </div>
            )}

            {type === 'continuous' && (
              <div className="space-y-2">
                <Label>Задержка перезапуска при сбое (мин)</Label>
                <Input type="number" min={0} max={60} value={restartDelay} onChange={e => setRestartDelay(+e.target.value)} className="w-32" />
                <p className="text-xs text-muted-foreground">Если проект упадёт, будет перезапущен через указанное время</p>
              </div>
            )}
          </div>

          <Separator />

          {/* Advanced settings */}
          <div className="space-y-3">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? '' : '-rotate-90'}`} />
              Дополнительные настройки
            </button>

            {showAdvanced && (
              <div className="space-y-4 pl-6">
                {/* Days */}
                <div className="space-y-2">
                  <Label>Дни работы</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {DAY_OPTIONS.map(d => (
                      <button
                        key={d.value}
                        onClick={() => setDays(d.value)}
                        className={`px-3 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                          days === d.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/30'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  {days === 'Выбранные дни' && (
                    <div className="flex gap-1 mt-2">
                      {WEEKDAYS.map(wd => (
                        <button
                          key={wd.key}
                          onClick={() => toggleCustomDay(wd.key)}
                          className={`w-9 h-9 text-xs rounded-md border transition-colors cursor-pointer ${
                            customDaysSet.has(wd.key)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/30'
                          }`}
                        >
                          {wd.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Work hours */}
                <div className="space-y-2">
                  <Label>Рабочие часы</Label>
                  <div className="flex items-center gap-2">
                    <Input type="time" value={workFrom} onChange={e => setWorkFrom(e.target.value)} className="w-28 tabular-nums" />
                    <span className="text-sm text-muted-foreground">—</span>
                    <Input type="time" value={workTo} onChange={e => setWorkTo(e.target.value)} className="w-28 tabular-nums" />
                  </div>
                  <p className="text-xs text-muted-foreground">Проект будет останавливаться вне рабочих часов</p>
                </div>

                {/* Duration (for non-interval types) */}
                {type !== 'interval' && type !== 'continuous' && (
                  <div className="space-y-2">
                    <Label>Макс. время работы за запуск</Label>
                    <div className="flex items-center gap-2">
                      <Input type="number" min={0} max={24} value={runHours} onChange={e => { const v = parseInt(e.target.value, 10); const c = isNaN(v) ? 0 : Math.min(Math.max(v, 0), 24); setRunHours(c); e.target.value = String(c) }} className="w-20" />
                      <span className="text-xs text-muted-foreground">ч</span>
                      <Input type="number" min={0} max={59} value={runMinutes} onChange={e => { const v = parseInt(e.target.value, 10); const c = isNaN(v) ? 0 : Math.min(Math.max(v, 0), 59); setRunMinutes(c); e.target.value = String(c) }} className="w-20" />
                      <span className="text-xs text-muted-foreground">мин</span>
                    </div>
                    <p className="text-xs text-muted-foreground">0 ч 0 мин = без ограничения</p>
                  </div>
                )}

                {/* Max runs */}
                <div className="space-y-2">
                  <Label>Макс. количество запусков</Label>
                  <Input type="number" min={0} value={maxRuns} onChange={e => setMaxRuns(+e.target.value)} className="w-32" />
                  <p className="text-xs text-muted-foreground">0 = без ограничения</p>
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-muted/50 border border-border p-3">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Итого</span>
            <p className="text-sm text-foreground mt-1">{summary}</p>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Сохранить расписание
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
